import { useEffect, useState } from "react"
import { isNull } from "lodash"
import { useTranslation } from "react-i18next"

import BasicButton from "@/components/button/BasicButton"
import Dialog from "@/components/dialog/Dialog"
import TextField from "@/components/form/textField/TextField"
import {
  useCreateSessionTypeMutation,
  useUpsertSessionTypeMutation,
} from "@/hooks/api/useTeamQuery"
import { createGlobalStore } from "@/stores/global"
import { useNativeBridge } from "@/hooks/useNativeBridge"

export default function AddSessionTypeDialog({
  openDialog,
  onCloseDialog,
  selectedIdfSessionType,
  sessionTypeTitle,
}: {
  openDialog: boolean
  onCloseDialog: () => void
  selectedIdfSessionType: number | null
  sessionTypeTitle: string
}) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState("")
  const [isFirstTouch, setIsFirstTouch] = useState(true)

  const setSnackBar = createGlobalStore((state) => state.setSnackBar)
  const { notifySettingChanged } = useNativeBridge()
  const { mutateAsync: createMutate } = useCreateSessionTypeMutation()
  const { mutateAsync: upsertMutate } = useUpsertSessionTypeMutation()

  useEffect(() => {
    setInputValue(sessionTypeTitle)
  }, [openDialog, sessionTypeTitle])

  const onSaveSessionType = async () => {
    notifySettingChanged("sessionType", "saving")
    try {
      if (isNull(selectedIdfSessionType)) {
        await createMutate({ name: inputValue })
      } else {
        await upsertMutate({ idfSessionType: selectedIdfSessionType, name: inputValue })
      }
      setSnackBar({
        severity: "success",
        message: t(`messages.${isNull(selectedIdfSessionType) ? "successAdd" : "successUpdate"}`),
        open: true,
      })
      notifySettingChanged("sessionType", isNull(selectedIdfSessionType) ? "create" : "update")
      onCloseDialog()
    } catch (error) {
      notifySettingChanged("sessionType", "error")

      return error
    }
  }

  const getSessionTypeTitleError = () => {
    if (inputValue === "") {
      return t("rules.required")
    }
    if (inputValue.length > 35) {
      return t("rules.max_len", { name: t("sessionType"), max_len: 35 })
    }

    return ""
  }

  return (
    <Dialog
      title={t(`messages.${isNull(selectedIdfSessionType) ? "newSessionType" : "editSessionType"}`)}
      open={openDialog}
      onClose={onCloseDialog}
      maxWidth="600px"
      footer={
        <div className="SessionTypeConfirmFooterCont">
          <BasicButton type="default-outlined" size="md" onClick={onCloseDialog}>
            {t("button.cancel")}
          </BasicButton>
          <BasicButton
            type="primary-contained"
            size="md"
            onClick={onSaveSessionType}
            disabled={!!getSessionTypeTitleError() || sessionTypeTitle === inputValue}>
            {t("button.save")}
          </BasicButton>
        </div>
      }>
      <div className="SessionTypeFormCont">
        <label>{t("sessionTitle")}</label>
        <TextField
          placeholder={t("messages.enterSessionTitle")}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          error={!!getSessionTypeTitleError() && !isFirstTouch}
          showMessage={!!getSessionTypeTitleError() && !isFirstTouch}
          message={getSessionTypeTitleError()}
          onBlur={() => {
            setIsFirstTouch(false)
          }}
        />
      </div>
    </Dialog>
  )
}
